/**
 * Created by rhinov15 on 28/01/2016.
 *
 * UI State where elements are selectable
 *
 */

import { UiState, UiStateEnum } from "./UiState";
import { EntityManager } from "../../plan/EntityManager";
import { GameObjectManager } from "../../plan/GameObjectManager";
import { PlanManager } from "../../../../cocosPlan";
import { Entity, Joinery, math, Room, SavaneConstants, SavaneMath, TechnicalElement, Wall } from "@rhinov/savane-js";

declare var cc: any;

export class SelectableElementsState extends UiState {
    constructor (cursorPosition: math.vec3, cocosPosition: math.vec3){
        super(cursorPosition, cocosPosition);
    }

    // Return value from gloabl enum
    getUiState(): number {
        return UiStateEnum.uiSelectableElements;
    }

    // Called when the mouse hovers a wall in this state, we can select this wall
    mouseHoverWall(wallHovered: Wall[], cursorPosition: math.vec3): boolean {
        // Pointer cursor type
        document.body.style.cursor = 'pointer';

        var planManager = PlanManager.getInstance();
        var wallNode;

        // Check if we hover a wall and the corner of this wall
        if (wallHovered.length === 1 && !wallHovered[0].isCorner(cursorPosition, SavaneConstants.PositionTolerance)) {
            // No, we are over a wall, inform it so it can show its hovered display
            wallNode = EntityManager.getInstance().getNode(wallHovered[0]);
            if (wallNode) {
                wallNode.setHovered(true);
                wallNode.enterMovable(cursorPosition, planManager.getZoomScale(), false);
            }
            // Make sure junction point (corner) hovering isn't displayed
            GameObjectManager.deleteJunctionPoint('HoverJunctionPoint');
        }
        else {
            var isCorner = false;
            var i;

            for (i = 0 ; i < wallHovered.length; i++) {
                if (wallHovered[0].isCorner(cursorPosition, SavaneConstants.PositionTolerance)) {
                    isCorner = true;
                    break;
                }
            }

            if (isCorner) {
                var point = wallHovered[i].isBegin(cursorPosition, SavaneConstants.PositionTolerance) ? wallHovered[i].begin : wallHovered[i].end;

                // We hover a junction point, so create it
                GameObjectManager.createJunctionPoint(new cc.p(point[0] / 10, point[1] / 10), planManager.getZoomScale(), 'HoverJunctionPoint', wallHovered);
                // Mark all hovered walls (that have the junction point in common) as movable
                for (i = 0; i < wallHovered.length; i++) {
                    wallNode = EntityManager.getInstance().getNode(wallHovered[i]);
                    if (wallNode != null) {
                        wallNode.enterMovable(cursorPosition, planManager.getZoomScale(), true);
                    }
                }
            }
            else {
                // Search for the wall we are phycically on
                for (i = 0; i < wallHovered.length; i++) {
                    var bbox = wallHovered[i].boundingBoxCut;

                    bbox.splice(1, 0, wallHovered[i].begin);
                    bbox.splice(4, 0, wallHovered[i].end);

                    if (SavaneMath.isInPoly(cursorPosition, bbox)) {
                        break;
                    }
                }
                // Securely found
                if (i < wallHovered.length) {
                    // Mark the wall hovered, inform it so it can show its hovered display
                    wallNode = EntityManager.getInstance().getNode(wallHovered[i])!;
                    wallNode.setHovered(true);
                    wallNode.enterMovable(cursorPosition, planManager.getZoomScale(), false);
                    // Make sure junction point (corner) hovering isn't displayed
                    GameObjectManager.deleteJunctionPoint('HoverJunctionPoint');
                }
            }
        }

        return true;
    }

    // Called when the mouse leaves a wall in this state
    mouseLeaveHoverWall(wallHovered: Wall[], cursorPosition: math.vec3): boolean {
        // Default cursor type
        document.body.style.cursor = 'default';
        // Make sure the junction point (corner) hovering isn't display anymore
        GameObjectManager.deleteJunctionPoint('HoverJunctionPoint');
        // Mark all hovered walls (that have the junction point in common) as not movable anymore
        for (var i = 0; i < wallHovered.length; i++) {
            // Hover effect should be removed
            var wallNode = EntityManager.getInstance().getNode(wallHovered[i]);
            if (wallNode != null) {
                wallNode.setHovered(false);
                wallNode.exitMovable();
            }
        }
        return true;
    }

    // Called when the mouse hovers a joinery in this state, it must show it is hovered
    mouseHoverJoinery(joinery: Joinery, cursorPosition: math.vec3): boolean {
        // Pointer cursor type
        document.body.style.cursor = 'pointer';
        // Mark joinery as hovered (so it shows its hovered display)
        var jNode = EntityManager.getInstance().getNode(joinery);
        if (jNode !== null) {
            // The joinery must belong to a wall
            if (joinery.wall != null) {
                jNode.setHovered(true);
            }
        }

        return true;
    }

    // Called when the mouse leaves a joinery in this state
    mouseLeaveHoverJoinery(joinery: Joinery, cursorPosition: math.vec3): boolean {
        // Default cursor type
        document.body.style.cursor = 'default';
        // Mark joinery as not hovered anymore
        var jNode = EntityManager.getInstance().getNode(joinery);
        if (jNode != null) {
            jNode.setHovered(false);
        }
        return true;
    }

    // Called when the mouse hovers a technical element in this state, it must show it is hovered
    mouseHoverTech(tech: TechnicalElement, cursorPosition: math.vec3): boolean {
        // Pointer cursor type
        document.body.style.cursor = 'pointer';
        // Mark joinery as hovered (so it shows its hovered display)
        var techNode = EntityManager.getInstance().getNode(tech);
        if (techNode != null) {
            techNode.setHovered(true);
        }
        return true;
    }

    // Called when the mouse leaves a technical element in this state
    mouseLeaveHoverTech(tech: TechnicalElement, cursorPosition: math.vec3): boolean {
        // Default cursor type
        document.body.style.cursor = 'default';
        // Mark technical element as not hovered anymore
        var techNode = EntityManager.getInstance().getNode(tech);
        if (techNode != null) {
            techNode.setHovered(false);
        }
        return true;
    }

    // Called when the mouse hovers a room in this state, it must show it is hovered
    mouseHoverRoom(room: Room, cursorPosition: math.vec3): boolean {
        // Pointer cursor type
        document.body.style.cursor = 'pointer';
        // Mark room as hovered (so it shows its hovered display)
        var roomNode = EntityManager.getInstance().getNode(room);
        if (roomNode !== null) {
            roomNode.setHovered(true);
        }
        return true;
    }

    // Called when the mouse leaves a room in this state
    mouseLeaveHoverRoom(room: Room, cursorPosition: math.vec3): boolean {
        // Default cursor type
        document.body.style.cursor = 'default';
        // Mark room as not hovered anymore
        var roomNode = EntityManager.getInstance().getNode(room);
        if (roomNode !== null) {
            roomNode.setHovered(false);
        }
        return true;
    }

    // Called when the mouse hovers an entity (camera, light, arrangement object...) in this state, it must show it is hovered
    mouseHoverEntity(entityHovered: Entity, cursorPosition: math.vec3): boolean {
        // Pointer cursor type
        document.body.style.cursor = 'pointer';
        // Mark entity as hovered (so it shows its hovered display)
        var node = EntityManager.getInstance().getNode(entityHovered);
        if (node !== null) {
            node.setHovered(true);
        }
        return true;
    }

    // Called when the mouse leaves an entity (camera, light, arrangement object...) in this state
    mouseLeaveHoverEntity(entityHovered: Entity): boolean {
        // Default cursor type
        document.body.style.cursor = 'default';
        // Mark entity as not hovered anymore
        var node = EntityManager.getInstance().getNode(entityHovered);
        if (node !== null) {
            node.setHovered(false);
        }
        return true;
    }

    // The mouse is moving in this state
    moveMouseCursor(cursorPosition: math.vec3, cocosPos: math.vec3): UiState | null {
        // Store cursor and cocos cursor positions
        this.cursorPosition = cursorPosition;
        this.cocosPosition = cocosPos;

        // If next ui state === current state, then execute setSelected method (inherited) then quit without changing state
        if (this.getNextUiState() === this.getUiState()) {
            this.setSelected();
            return null;
        }
        else {
            //  Create next Ui state for next loop and return it for activation
            return this.createNextUiState();
        }
    }

    // Ui state destruction, delete potential remaining objects (junction, joineries extremities), this uses cummon objects in the game object manager
    dispose() {
        GameObjectManager.deleteJunctionPoint('HoverJunctionPoint');
        GameObjectManager.deleteJoineryExtremity('SelectedJoineryPoint');
        GameObjectManager.deleteJoineryExtremity('SelectedJoineryPoint2');

        // Call parent to potentially deactivate hovering
        super.dispose();
    }
}
